import React, { useState } from 'react';
import { Paperclip, Loader2, X, Plus, Camera } from 'lucide-react';
import { documentService } from '../../../../services/documentos/documentService';

export const ExecutionModalAttachments = ({
  workOrder,
  task,
  documentTypes = [],
  queryClient,
  formRef,
  isCompleted
}) => {
  const [localModalError, setLocalModalError] = useState(null);
  const [selectedFile, setSelectedFile] = useState(null);
  const [documentType, setDocumentType] = useState('');
  const [description, setDescription] = useState('');
  const [uploadProgress, setUploadProgress] = useState(0);
  const [localIsSubmitting, setLocalIsSubmitting] = useState(false);
  const [isFabOpen, setIsFabOpen] = useState(false);

  // File Select
  const handleFileChange = (e) => {
    const file = e.target.files?.[0];
    if (file) {
      setSelectedFile(file);
    }
    // Clear input so the same file can be selected again
    e.target.value = null;
  };

  const handleClose = () => {
    if (!localIsSubmitting) {
      setDocumentType('');
      setDescription('');
      setSelectedFile(null);
      setLocalModalError(null);
      setUploadProgress(0);
    }
  };

  const handleSubmitDocument = async (e) => {
    e.preventDefault();
    setLocalModalError(null);

    if (!documentType.trim()) {
      setLocalModalError('Document type is required');
      formRef.current?.scrollTo({ top: 0, behavior: 'smooth' });
      return;
    }
    if (!selectedFile) {
      setLocalModalError('Please select a file to upload');
      formRef.current?.scrollTo({ top: 0, behavior: 'smooth' });
      return;
    }

    setLocalIsSubmitting(true);
    setUploadProgress(0);
    try {
      const formData = new FormData();
      formData.append('WorkOrderID', String(workOrder.id));
      formData.append('MaintTaskID', String(task.id))
      formData.append('DocumentType', documentType);
      formData.append('Description', description);
      formData.append('document', selectedFile);
      console.log(formData, 'Upload Form Data')
      await documentService.uploadWorkOrderDocument(formData, ({ percent }) => setUploadProgress(percent));
      queryClient.invalidateQueries({ queryKey: ['wo'] });
      handleClose();
    } catch (err) {
      const serverMsg = err.response?.data?.message ?? err.response?.data?.error ?? err.message;
      const status = err.response?.status;
      const debugMsg = status ? `[${status}] ${serverMsg}` : serverMsg;
      console.error('[UploadDocument] Error:', { err, response: err.response });
      setLocalModalError(debugMsg || 'Failed to upload document');
      setUploadProgress(0);
      formRef.current?.scrollTo({ top: 0, behavior: 'smooth' });
    } finally {
      setLocalIsSubmitting(false);
    }
  };

  return (
    <div className="space-y-4">
      {localModalError && (
        <div className="rounded-lg border border-red-800 bg-red-900/20 p-4 mb-4">
          <p className="text-red-300 text-sm">{localModalError}</p>
        </div>
      )}

      <h3 className="text-lg font-semibold text-white border-b border-gray-700 pb-2">
        Attachments
      </h3>
      {!isCompleted && (
        <div className="space-y-4">
          {/* File input (Desktop only) */}
          <div className="hidden md:block">
            <label className="block text-sm font-medium text-gray-300 mb-2">
              Select Document
            </label>
            <div className="flex items-center gap-4">
              <label className={`
              flex items-center gap-2 px-4 py-2 bg-gray-700 border border-gray-600 
              rounded-lg cursor-pointer hover:bg-gray-600 transition-colors
              ${localIsSubmitting ? 'opacity-50 cursor-not-allowed' : ''}
            `}>
                <Paperclip className="w-4 h-4 text-gray-400" />
                <span className="text-sm text-gray-300">Choose File</span>
                <input
                  type="file"
                  className="hidden"
                  onChange={handleFileChange}
                  disabled={localIsSubmitting}
                  accept=".pdf,.doc,.docx,.xls,.xlsx,.png,.jpg,.jpeg"
                />
              </label>
              {selectedFile && (
                <span className="text-sm text-gray-400 truncate max-w-xs">
                  {selectedFile.name}
                </span>
              )}
            </div>
          </div>

          {selectedFile && (
            <div className="space-y-4 pt-4 border-t border-gray-700">
              <div>
                <label className="block text-sm font-medium text-gray-300 mb-1">
                  Document Type <span className="text-red-500">*</span>
                </label>
                <select
                  className="w-full bg-gray-700 border border-gray-600 rounded-lg px-4 py-2 text-white focus:outline-none focus:ring-2 focus:ring-blue-500"
                  value={documentType}
                  onChange={(e) => setDocumentType(e.target.value)}
                  disabled={localIsSubmitting}
                >
                  <option value="">Select a type...</option>
                  {documentTypes.map(type => (
                    <option key={type.name} value={type.name}>
                      {type.name}
                    </option>
                  ))}
                </select>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-300 mb-1">
                  Description
                </label>
                <textarea
                  className="w-full bg-gray-700 border border-gray-600 rounded-lg px-4 py-2 text-white focus:outline-none focus:ring-2 focus:ring-blue-500"
                  rows={3}
                  placeholder="Optional description"
                  value={description}
                  onChange={(e) => setDescription(e.target.value)}
                  disabled={localIsSubmitting}
                />
              </div>

              {localIsSubmitting && (
                <div className="space-y-2">
                  <div className="flex justify-between text-sm text-gray-400">
                    <span>Uploading...</span>
                    <span>{Math.round(uploadProgress)}%</span>
                  </div>
                  <div className="w-full bg-gray-700 rounded-full h-2">
                    <div
                      className="bg-blue-500 h-2 rounded-full transition-all duration-300"
                      style={{ width: `${uploadProgress}%` }}
                    />
                  </div>
                </div>
              )}

              <div className="flex items-center justify-end gap-3 pt-4">
                <button
                  type="button"
                  onClick={handleSubmitDocument}
                  className="px-4 py-2 rounded-lg border border-blue-600 bg-blue-600 text-white text-sm font-medium hover:bg-blue-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed inline-flex items-center gap-2"
                  disabled={localIsSubmitting || !documentType}
                >
                  {localIsSubmitting ? (
                    <>
                      <Loader2 className="w-4 h-4 animate-spin" />
                      Uploading...
                    </>
                  ) : (
                    'Upload'
                  )}
                </button>
              </div>
            </div>
          )}
        </div>
      )}

      {/* Mobile Floating Action Button (FAB) */}
      {!isCompleted && !selectedFile && (
        <div className="md:hidden bottom-6 right-6 z-50 flex flex-col items-end gap-3">
          {/* Expanded Menu Options */}
          {isFabOpen && (
            <div className="flex flex-col items-end gap-3 mb-2 animate-in slide-in-from-bottom-5 fade-in duration-200">
              <label className="flex items-center gap-3 bg-gray-700 border border-gray-600 px-4 py-2 rounded-full shadow-lg cursor-pointer hover:bg-gray-600 transition-colors">
                <span className="text-sm font-medium text-white">Take Photo</span>
                <div className="w-8 h-8 rounded-full bg-blue-600 flex items-center justify-center">
                  <Camera className="w-4 h-4 text-white" />
                </div>
                <input
                  type="file"
                  className="hidden"
                  onChange={(e) => {
                    handleFileChange(e);
                    setIsFabOpen(false);
                  }}
                  disabled={localIsSubmitting}
                  accept="image/*"
                  capture="environment"
                />
              </label>

              <label className="flex items-center gap-3 bg-gray-700 border border-gray-600 px-4 py-2 rounded-full shadow-lg cursor-pointer hover:bg-gray-600 transition-colors">
                <span className="text-sm font-medium text-white">Browse Files</span>
                <div className="w-8 h-8 rounded-full bg-gray-600 flex items-center justify-center">
                  <Paperclip className="w-4 h-4 text-gray-300" />
                </div>
                <input
                  type="file"
                  className="hidden"
                  onChange={(e) => {
                    handleFileChange(e);
                    setIsFabOpen(false);
                  }}
                  disabled={localIsSubmitting}
                  accept=".pdf,.doc,.docx,.xls,.xlsx,.png,.jpg,.jpeg"
                />
              </label>
            </div>
          )}

          {/* Main FAB Toggle */}
          <button
            type="button"
            onClick={() => setIsFabOpen(!isFabOpen)}
            className={`w-14 h-14 rounded-full flex items-center justify-center shadow-xl transition-all duration-300 ${isFabOpen ? 'bg-red-600 rotate-45 hover:bg-red-700' : 'bg-blue-600 hover:bg-blue-700'
              }`}
          >
            <Plus className="w-6 h-6 text-white" />
          </button>
        </div>
      )}

      {task?.documents?.length > 0 && (
        <div className="mt-8 pt-6 border-t border-gray-700">
          <h4 className="text-md font-medium text-gray-300 mb-4">Existing Documents</h4>
          <div className="space-y-3">
            {task.documents.map((doc) => (
              <div key={doc.id} className="flex items-center justify-between p-3 bg-gray-750 border border-gray-600 rounded-lg">
                <div className="flex items-center gap-3">
                  <div className="p-2 bg-gray-700 rounded text-blue-400">
                    <Paperclip className="w-5 h-5" />
                  </div>
                  <div>
                    <p className="text-sm font-medium text-white">{doc.name || 'Unnamed Document'}</p>
                    <p className="text-xs text-gray-400">{doc.description || 'No description'}</p>
                  </div>
                </div>
                <div className="flex items-center gap-2">
                  <span className={`px-2 py-1 text-xs rounded-full font-medium ${doc.status === 'success' || doc.status === 'completed'
                    ? 'bg-green-900/30 text-green-400 border border-green-800'
                    : doc.status === 'pending' || doc.status === 'processing'
                      ? 'bg-yellow-900/30 text-yellow-400 border border-yellow-800'
                      : 'bg-red-900/30 text-red-400 border border-red-800'
                    }`}>
                    {doc.status}
                  </span>
                  {(doc.status === 'success' || doc.status === 'completed') && (
                    <button
                      type="button"
                      onClick={() => window.open(documentService.getDocumentContentUrl(doc.id, { download: false }), '_blank')}
                      className="p-1.5 hover:bg-gray-600 text-blue-400 hover:text-blue-300 rounded transition-colors"
                      title="View Document"
                    >
                      <Paperclip className="w-4 h-4" />
                    </button>
                  )}
                </div>
              </div>
            ))}
          </div>
        </div>
      )}


    </div>
  );
};
